/**
 * BulkActionsMenu — V36.20
 *
 * Dropdown unique "Actions en masse" pour la barre de sélection des nomenclatures.
 * Regroupe : Lier dispositifs, Lier estimateurs, Changer statut, Archiver,
 * Désarchiver, Dupliquer dans une collection.
 *
 * Affiche des sous-modales pour les actions nécessitant un input.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, Building, Calculator, Tag, Archive, ArchiveRestore, Copy, Loader2, Layers, UserCheck, UserMinus,
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { getAuthHeader } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Actif' },
  { value: 'inactive', label: 'Inactif' },
  { value: 'pending', label: 'En attente' },
  { value: 'archived', label: 'Archivé' },
];

export default function BulkActionsMenu({
  selectedNomenclatures = [],
  collections = [],
  onApplied,
  onOpenBulkLink,
}) {
  const count = selectedNomenclatures.length;
  const ids = selectedNomenclatures.map((n) => n.id);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusValue, setStatusValue] = useState('active');

  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState('');
  const [duplicateKeepLinks, setDuplicateKeepLinks] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const callBulk = async (endpoint, body, successMsg) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}${endpoint}`, body, { headers: getAuthHeader() });
      toast.success(successMsg(res.data));
      onApplied?.();
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
      throw e;
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatus = async () => {
    await callBulk(
      '/nomenclatures/bulk-status',
      { nomenclature_ids: ids, status: statusValue },
      (d) => `Statut "${statusValue}" appliqué à ${d.updated} nomenclature${d.updated > 1 ? 's' : ''}`,
    );
    setStatusOpen(false);
  };

  const handleArchive = async (archive) => {
    await callBulk(
      '/nomenclatures/bulk-archive',
      { nomenclature_ids: ids, archive },
      (d) => `${d.updated} nomenclature${d.updated > 1 ? 's' : ''} ${archive ? 'archivée(s)' : 'désarchivée(s)'}`,
    );
  };

  const handleDevenirMembre = async (applicable) => {
    await callBulk(
      '/nomenclatures/bulk-devenir-membre',
      { nomenclature_ids: ids, applicable, status: 'active' },
      (d) => `${d.updated} nomenclature${d.updated > 1 ? 's' : ''} ${applicable ? 'liée(s) à « Devenir Membre »' : 'retirée(s) du workflow « Devenir Membre »'}`,
    );
  };

  const handleDuplicate = async () => {
    if (!duplicateTarget) {
      toast.error('Sélectionnez une collection cible');
      return;
    }
    await callBulk(
      '/nomenclatures/bulk-duplicate',
      {
        nomenclature_ids: ids,
        target_collection_id: duplicateTarget,
        keep_links: duplicateKeepLinks,
      },
      (d) => `${d.created} nomenclature${d.created > 1 ? 's' : ''} dupliquée${d.created > 1 ? 's' : ''} dans « ${d.target_collection} »`,
    );
    setDuplicateOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
            data-testid="bulk-actions-trigger"
          >
            <Layers className="h-4 w-4 mr-1.5" />
            Actions en masse ({count})
            <ChevronDown className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60" data-testid="bulk-actions-menu">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
            Liaisons
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => onOpenBulkLink?.('dispositifs')}
            data-testid="bulk-action-dispositifs"
          >
            <Building className="h-4 w-4 mr-2 text-indigo-600" />
            Lier dispositifs
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onOpenBulkLink?.('estimateurs')}
            data-testid="bulk-action-estimateurs"
          >
            <Calculator className="h-4 w-4 mr-2 text-emerald-600" />
            Lier estimateurs
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleDevenirMembre(true)}
            disabled={submitting}
            data-testid="bulk-action-devenir-membre"
          >
            <UserCheck className="h-4 w-4 mr-2 text-blue-700" />
            Lier à « Devenir Membre »
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleDevenirMembre(false)}
            disabled={submitting}
            className="text-slate-600"
            data-testid="bulk-action-devenir-membre-remove"
          >
            <UserMinus className="h-4 w-4 mr-2" />
            Retirer de « Devenir Membre »
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
            Cycle de vie
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => setStatusOpen(true)}
            data-testid="bulk-action-status"
          >
            <Tag className="h-4 w-4 mr-2 text-amber-600" />
            Changer le statut
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleArchive(true)}
            className="text-orange-700"
            data-testid="bulk-action-archive"
          >
            <Archive className="h-4 w-4 mr-2" />
            Archiver
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleArchive(false)}
            data-testid="bulk-action-unarchive"
          >
            <ArchiveRestore className="h-4 w-4 mr-2 text-slate-600" />
            Désarchiver
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setDuplicateTarget('');
              setDuplicateKeepLinks(true);
              setDuplicateOpen(true);
            }}
            data-testid="bulk-action-duplicate"
          >
            <Copy className="h-4 w-4 mr-2 text-purple-600" />
            Dupliquer dans une collection…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog Changer le statut */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Changer le statut</DialogTitle>
            <DialogDescription>
              Appliquer un nouveau statut à <strong>{count}</strong> nomenclature{count > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Select value={statusValue} onValueChange={setStatusValue}>
              <SelectTrigger data-testid="bulk-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button
              onClick={handleStatus}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-status-confirm"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Dupliquer */}
      <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Dupliquer dans une collection</DialogTitle>
            <DialogDescription>
              Crée <strong>{count}</strong> nouvelle{count > 1 ? 's' : ''} nomenclature{count > 1 ? 's' : ''} dans la collection cible.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Collection cible <span className="text-red-500">*</span>
              </label>
              <Select value={duplicateTarget} onValueChange={setDuplicateTarget}>
                <SelectTrigger data-testid="bulk-duplicate-target">
                  <SelectValue placeholder="Choisir une collection…" />
                </SelectTrigger>
                <SelectContent>
                  {collections.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name || c.nom || c.code || c.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={duplicateKeepLinks}
                onChange={(e) => setDuplicateKeepLinks(e.target.checked)}
                className="mt-0.5"
                data-testid="bulk-duplicate-keep-links"
              />
              <span className="text-sm text-slate-700 leading-snug">
                Conserver les liaisons dispositifs / estimateurs sur les copies
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateOpen(false)} disabled={submitting}>
              Annuler
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={submitting || !duplicateTarget}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="bulk-duplicate-confirm"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Dupliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
